import { Formik, Form, useField } from 'formik';
import * as Yup from 'yup';
import { KoobButton } from '@koob/ui';
import { useGoogleFonts } from './googleFonts';
import FontPickerField from './FontPickerField';
import BrandingPreviewCard from './BrandingPreviewCard';
import { resolveTravelDiaryTheme } from './branding';

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
const FONT_FAMILY_REGEX = /^[A-Za-z0-9 -]{1,100}$/;
const SUPPORTED_LOGO_FORMATS = ['image/png', 'image/jpeg', 'image/svg+xml'];

const DEFAULT_LABELS = {
  kicker: 'Tour Operator theme',
  title: 'Travel Diary branding',
  subtitle:
    'Customize the colors, logo and fonts applied to every Travel Diary generated for your agency.',
  logo: 'Logo',
  logoHelp: 'JPG, SVG or PNG. Square image, 256×256px.',
  logoFormatError: 'Accepted formats: PNG, JPG, SVG',
  chooseFile: 'Choose file',
  palette: 'Palette',
  primaryColor: 'Primary',
  primaryCaption: 'Headings & buttons',
  inkColor: 'Ink',
  inkCaption: 'Accents & links',
  paperColor: 'Paper',
  paperCaption: 'Page background',
  colorFormatError: 'Must be a 6-digit hex color',
  typography: 'Typography',
  displayFont: 'Display font',
  bodyFont: 'Body font',
  fontFormatError: 'Letters, digits, spaces and hyphens only',
  displaySample: 'Bangkok → Chiang Mai',
  bodySample: 'Day 01 · Departure at 08:45',
  livePreview: 'Live preview',
  save: 'Save',
  reset: 'Reset',
  previewSubtitle: 'Your Travel Diary, in your colors and fonts.',
};

function SectionLabel({ children }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
      {children}
    </div>
  );
}

function ColorSwatchField({ label, caption, name, errorMessage }) {
  const [field, meta, helpers] = useField(name);
  const isValid = HEX_COLOR_REGEX.test(field.value);
  const swatch = isValid ? field.value : '#FFFFFF';
  const hasError = Boolean(meta.touched) && Boolean(meta.error);

  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 focus-within:border-slate-400">
        <label
          className="relative h-6 w-6 shrink-0 cursor-pointer overflow-hidden rounded-md border border-black/5"
          style={{ background: swatch }}
        >
          <input
            type="color"
            value={swatch}
            onChange={(event) =>
              helpers.setValue(event.target.value.toUpperCase())
            }
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>
        <input
          type="text"
          value={field.value}
          onBlur={field.onBlur}
          onChange={(event) => helpers.setValue(event.target.value)}
          placeholder="#000000"
          className="w-full min-w-0 border-none p-0 text-sm text-slate-700 outline-none"
        />
      </div>
      {hasError ? (
        <p className="mt-1.5 text-xs text-red-500">{errorMessage}</p>
      ) : (
        <p className="mt-1.5 text-xs text-slate-400">{caption}</p>
      )}
    </div>
  );
}

function LogoField({
  label,
  help,
  formatError,
  chooseFileLabel,
  name,
  logoUrl,
}) {
  const [field, meta, helpers] = useField(name);
  const hasError = Boolean(meta.touched) && Boolean(meta.error);
  const previewUrl =
    field.value === null ? null : (field.value?.imageUrl ?? logoUrl);

  const handleFile = (event) => {
    const [file] = event.target.files;
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      helpers.setValue({
        file,
        displayName: file.name,
        imageUrl: reader.result,
      });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-3 flex items-center gap-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <i className="fa-regular fa-cloud-arrow-up text-xl text-slate-400" />
          )}
        </div>
        <div>
          <div className="flex items-center gap-3">
            <label className="cursor-pointer rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-300">
              {chooseFileLabel}
              <input
                type="file"
                accept={SUPPORTED_LOGO_FORMATS.join(',')}
                onChange={handleFile}
                onClick={(event) => {
                  event.target.value = null;
                }}
                className="hidden"
              />
            </label>
            {Boolean(field.value) && (
              <button
                type="button"
                onClick={() => helpers.setValue(null)}
                className="border-none bg-transparent p-0 text-slate-400 outline-none hover:text-slate-600 focus:outline-none focus-visible:outline-none"
                aria-label="Remove logo"
              >
                <i className="fa-regular fa-trash-can" />
              </button>
            )}
          </div>
          <p className="mt-1.5 text-xs text-slate-400">{help}</p>
          {hasError && (
            <p className="mt-1 text-xs text-red-500">{formatError}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TravelDiaryBrandingForm({
  initialValues,
  organizationName,
  logoUrl,
  fetchFonts,
  onSubmit,
  labels: labelOverrides,
}) {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const { fonts, isLoading: isLoadingFonts } = useGoogleFonts(fetchFonts);
  const formInitialValues = {
    ...initialValues,
    ...resolveTravelDiaryTheme(initialValues),
  };

  const validationSchema = Yup.object().shape({
    primaryColor: Yup.string().matches(HEX_COLOR_REGEX, {
      message: labels.colorFormatError,
      excludeEmptyString: true,
    }),
    inkColor: Yup.string().matches(HEX_COLOR_REGEX, {
      message: labels.colorFormatError,
      excludeEmptyString: true,
    }),
    paperColor: Yup.string().matches(HEX_COLOR_REGEX, {
      message: labels.colorFormatError,
      excludeEmptyString: true,
    }),
    displayFont: Yup.string().matches(FONT_FAMILY_REGEX, {
      message: labels.fontFormatError,
      excludeEmptyString: true,
    }),
    bodyFont: Yup.string().matches(FONT_FAMILY_REGEX, {
      message: labels.fontFormatError,
      excludeEmptyString: true,
    }),
    avatar: Yup.mixed().test('fileFormat', labels.logoFormatError, (value) => {
      const file = value?.file || value;
      return !file?.type || SUPPORTED_LOGO_FORMATS.includes(file.type);
    }),
  });

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">
      <div className="border-b border-slate-100 px-10 py-10">
        <div className="text-xs font-semibold uppercase tracking-wider text-orange-500">
          {labels.kicker}
        </div>
        <h1 className="mt-2 font-serif text-3xl font-bold text-slate-900">
          {labels.title}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-slate-500">
          {labels.subtitle}
        </p>
      </div>

      <Formik
        initialValues={formInitialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ values, resetForm, dirty, isSubmitting }) => (
          <Form>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="flex flex-col gap-8 p-10">
                <LogoField
                  label={labels.logo}
                  help={labels.logoHelp}
                  formatError={labels.logoFormatError}
                  chooseFileLabel={labels.chooseFile}
                  name="avatar"
                  logoUrl={logoUrl}
                />

                <div className="border-t border-slate-100 pt-8">
                  <SectionLabel>{labels.palette}</SectionLabel>
                  <div className="mt-3 grid grid-cols-3 gap-4">
                    <ColorSwatchField
                      label={labels.primaryColor}
                      caption={labels.primaryCaption}
                      errorMessage={labels.colorFormatError}
                      name="primaryColor"
                    />
                    <ColorSwatchField
                      label={labels.inkColor}
                      caption={labels.inkCaption}
                      errorMessage={labels.colorFormatError}
                      name="inkColor"
                    />
                    <ColorSwatchField
                      label={labels.paperColor}
                      caption={labels.paperCaption}
                      errorMessage={labels.colorFormatError}
                      name="paperColor"
                    />
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-8">
                  <div className="flex items-center justify-between">
                    <SectionLabel>{labels.typography}</SectionLabel>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <FontPickerField
                      label={labels.displayFont}
                      name="displayFont"
                      fonts={fonts}
                      isLoadingFonts={isLoadingFonts}
                      sampleText={labels.displaySample}
                    />
                    <FontPickerField
                      label={labels.bodyFont}
                      name="bodyFont"
                      fonts={fonts}
                      isLoadingFonts={isLoadingFonts}
                      sampleText={labels.bodySample}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50 p-10 lg:border-l lg:border-t-0">
                <SectionLabel>{labels.livePreview}</SectionLabel>
                <BrandingPreviewCard
                  values={values}
                  title={organizationName || 'Organization Name'}
                  subtitle={labels.previewSubtitle}
                  logoUrl={logoUrl}
                />
                <div className="flex items-center justify-end gap-3 pt-2">
                  <KoobButton
                    type="button"
                    variant="secondary"
                    onClick={() => resetForm()}
                  >
                    {labels.reset}
                  </KoobButton>
                  <KoobButton
                    type="submit"
                    variant="primary"
                    disabled={!dirty || isSubmitting}
                    loading={isSubmitting}
                  >
                    {labels.save}
                  </KoobButton>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
